import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  getAdminCase,
  upsertCase,
  deleteCase,
  duplicateCase,
  type AdminCase,
} from "@/lib/admin-cases.functions";

export const Route = createFileRoute("/_authenticated/admin/cases/$id")({
  head: () => ({
    meta: [
      { title: "Redigera case — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CaseEdit,
});

type Form = {
  id?: string;
  slug: string;
  client_name: string;
  client_industry: string | null;
  format: string;
  city: string | null;
  budget_range: string | null;
  lead_time: string | null;
  deliverables: string[];
  outcome_metric: string | null;
  outcome_detail: string | null;
  hero_image_url: string | null;
  video_url: string | null;
  client_quote: string | null;
  client_quote_author: string | null;
  client_quote_role: string | null;
  body: string | null;
  sort_index: number;
  status: "draft" | "published";
  published_at?: string | null;
};

const EMPTY: Form = {
  slug: "",
  client_name: "",
  client_industry: "",
  format: "",
  city: "",
  budget_range: "",
  lead_time: "",
  deliverables: [],
  outcome_metric: "",
  outcome_detail: "",
  hero_image_url: "",
  video_url: "",
  client_quote: "",
  client_quote_author: "",
  client_quote_role: "",
  body: "",
  sort_index: 0,
  status: "draft",
  published_at: null,
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/å|ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function fromRow(p: AdminCase): Form {
  return {
    id: p.id,
    slug: p.slug,
    client_name: p.client_name,
    client_industry: p.client_industry,
    format: p.format,
    city: p.city,
    budget_range: p.budget_range,
    lead_time: p.lead_time,
    deliverables: p.deliverables ?? [],
    outcome_metric: p.outcome_metric,
    outcome_detail: p.outcome_detail,
    hero_image_url: p.hero_image_url,
    video_url: p.video_url,
    client_quote: p.client_quote,
    client_quote_author: p.client_quote_author,
    client_quote_role: p.client_quote_role,
    body: p.body,
    sort_index: p.sort_index,
    status: p.status === "published" ? "published" : "draft",
    published_at: p.published_at,
  };
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-white/50 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-white/40 mt-1">{hint}</span>}
    </label>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 border-b border-white/10 pb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

const inputCls =
  "w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-white/30";

function CaseEdit() {
  const { id } = Route.useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const getFn = useServerFn(getAdminCase);
  const saveFn = useServerFn(upsertCase);
  const delFn = useServerFn(deleteCase);
  const dupFn = useServerFn(duplicateCase);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "cases", id],
    queryFn: () => getFn({ data: { id } }),
    enabled: !isNew,
  });

  const [form, setForm] = useState<Form>(EMPTY);
  const [slugDirty, setSlugDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.row) {
      setForm(fromRow(data.row));
      setSlugDirty(true);
    }
  }, [data]);

  const update = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isNew && !slugDirty && form.client_name) {
      setForm((p) => ({ ...p, slug: slugify(p.client_name) }));
    }
  }, [form.client_name, isNew, slugDirty]);

  const validationErrors = useMemo(() => {
    const e: string[] = [];
    if (!form.client_name.trim()) e.push("Klient saknas");
    if (!form.slug.trim()) e.push("Slug saknas");
    else if (!/^[a-z0-9-]+$/.test(form.slug)) e.push("Slug får bara innehålla a-z, 0-9 och bindestreck");
    if (!form.format.trim()) e.push("Format saknas");
    const isUrl = (s: string) => {
      try { new URL(s); return true; } catch { return false; }
    };
    if (form.hero_image_url && form.hero_image_url.trim() && !isUrl(form.hero_image_url.trim()))
      e.push("Hero-bild är inte en giltig URL");
    if (form.video_url && form.video_url.trim() && !isUrl(form.video_url.trim()))
      e.push("Video-URL är inte giltig");
    return e;
  }, [form]);

  const handleSave = async () => {
    setErr(null);
    setSaved(false);
    if (validationErrors.length) {
      setErr(validationErrors.join(" • "));
      return;
    }
    setSaving(true);
    try {
      const cleanStr = (v: string | null | undefined) =>
        v && v.trim() ? v.trim() : null;
      const payload: Record<string, unknown> = {
        slug: form.slug.trim(),
        client_name: form.client_name.trim(),
        client_industry: cleanStr(form.client_industry),
        format: form.format.trim(),
        city: cleanStr(form.city),
        budget_range: cleanStr(form.budget_range),
        lead_time: cleanStr(form.lead_time),
        deliverables: form.deliverables.length ? form.deliverables : null,
        outcome_metric: cleanStr(form.outcome_metric),
        outcome_detail: cleanStr(form.outcome_detail),
        hero_image_url: cleanStr(form.hero_image_url),
        video_url: cleanStr(form.video_url),
        client_quote: cleanStr(form.client_quote),
        client_quote_author: cleanStr(form.client_quote_author),
        client_quote_role: cleanStr(form.client_quote_role),
        body: form.body && form.body.trim() ? form.body : null,
        sort_index: Math.max(0, Math.min(9999, Math.round(form.sort_index))),
        status: form.status,
      };
      if (!isNew && form.id) payload.id = form.id;
      if (form.published_at) payload.published_at = form.published_at;

      const res = await saveFn({ data: payload as never });
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      qc.invalidateQueries({ queryKey: ["admin", "cases", id] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (isNew) navigate({ to: "/admin/cases/$id", params: { id: res.id } });
    } catch (e) {
      console.error("[admin.cases] save failed", e);
      setErr(e instanceof Error ? e.message : "Något gick fel vid sparning");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    if (!confirm(`Radera caset "${form.client_name}"? Kan inte ångras.`)) return;
    try {
      await delFn({ data: { id: form.id } });
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      navigate({ to: "/admin/cases" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kunde inte radera");
    }
  };

  const handleDuplicate = async () => {
    if (!form.id) return;
    try {
      const res = await dupFn({ data: { id: form.id } });
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      navigate({ to: "/admin/cases/$id", params: { id: res.id } });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kunde inte duplicera");
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  if (!isNew && isLoading) {
    return <div className="p-6 md:p-8 text-white/60">Laddar case…</div>;
  }

  return (
    <div className="p-6 md:p-8 max-w-5xl space-y-8">
      <Link to="/admin/cases" className="text-sm text-white/60 hover:text-white">← Tillbaka</Link>

      <header className="flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10 bg-background/70 backdrop-blur py-2 -mx-2 px-2 rounded-lg">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isNew ? "Nytt case" : form.client_name || "Redigera case"}
          </h1>
          <p className="text-xs text-white/40 mt-0.5">
            {form.status === "published" ? "Publicerad" : "Utkast"}
            {form.published_at && ` • ${new Date(form.published_at).toLocaleString("sv-SE")}`}
            {!isNew && form.slug && (
              <>
                {" • "}
                <a
                  href={`/case/${form.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-white"
                >
                  Förhandsgranska ↗
                </a>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-xs text-emerald-300">Sparat ✓</span>}
          {!isNew && (
            <>
              <button
                onClick={handleDuplicate}
                className="rounded-full border border-white/15 text-white/80 hover:bg-white/5 px-4 py-2 text-sm"
              >
                Duplicera
              </button>
              <button
                onClick={handleDelete}
                className="rounded-full border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 px-4 py-2 text-sm"
              >
                Radera
              </button>
            </>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-mint px-5 py-2 text-sm font-medium text-mint-foreground disabled:opacity-50"
          >
            {saving ? "Sparar…" : "Spara (⌘S)"}
          </button>
        </div>
      </header>

      {err && (
        <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-200 whitespace-pre-wrap">
          {err}
        </div>
      )}

      <Section title="Grunddata">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Klient"><input className={inputCls} value={form.client_name} onChange={(e) => update("client_name", e.target.value)} /></Field>
          <Field label="Slug" hint="a-z, 0-9, bindestreck">
            <input
              className={inputCls}
              value={form.slug}
              onChange={(e) => { setSlugDirty(true); update("slug", e.target.value); }}
            />
          </Field>
          <Field label="Bransch"><input className={inputCls} value={form.client_industry ?? ""} onChange={(e) => update("client_industry", e.target.value)} /></Field>
          <Field label="Format" hint="ex. Reklamfilm, Produktfilm, Event">
            <input className={inputCls} value={form.format} onChange={(e) => update("format", e.target.value)} />
          </Field>
          <Field label="Stad"><input className={inputCls} value={form.city ?? ""} onChange={(e) => update("city", e.target.value)} /></Field>
          <Field label="Budgetspann"><input className={inputCls} value={form.budget_range ?? ""} onChange={(e) => update("budget_range", e.target.value)} /></Field>
          <Field label="Ledtid"><input className={inputCls} value={form.lead_time ?? ""} onChange={(e) => update("lead_time", e.target.value)} /></Field>
          <Field label="Sorteringsindex" hint="Lägre värde visas först">
            <input type="number" min={0} max={9999} className={inputCls} value={form.sort_index} onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              update("sort_index", Number.isFinite(n) ? n : 0);
            }} />
          </Field>
          <Field label="Status">
            <select className={inputCls} value={form.status} onChange={(e) => update("status", e.target.value as Form["status"])}>
              <option value="draft">Utkast</option>
              <option value="published">Publicerad</option>
            </select>
          </Field>
        </div>
        <Field label="Deliverables (komma-separerade)" hint="ex. Reklamfilm 30s, Klipp för social, Stillbilder">
          <input
            className={inputCls}
            value={form.deliverables.join(", ")}
            onChange={(e) => update("deliverables", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          />
          {form.deliverables.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {form.deliverables.map((d) => (
                <span key={d} className="text-[11px] rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-white/70">
                  {d}
                </span>
              ))}
            </div>
          )}
        </Field>
      </Section>

      <Section title="Media">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Hero-bild URL">
            <input className={inputCls} value={form.hero_image_url ?? ""} onChange={(e) => update("hero_image_url", e.target.value)} placeholder="https://…" />
          </Field>
          <Field label="Video URL" hint="Vimeo / YouTube / direkt mp4">
            <input className={inputCls} value={form.video_url ?? ""} onChange={(e) => update("video_url", e.target.value)} placeholder="https://…" />
          </Field>
        </div>
        {form.hero_image_url && (
          <div className="rounded-lg border border-white/10 overflow-hidden bg-white/5">
            <img
              src={form.hero_image_url}
              alt="Förhandsgranskning"
              className="max-h-64 w-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          </div>
        )}
      </Section>

      <Section title="Resultat">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Resultat-metric" hint="ex. +312% reach"><input className={inputCls} value={form.outcome_metric ?? ""} onChange={(e) => update("outcome_metric", e.target.value)} /></Field>
          <Field label="Resultat-detalj"><input className={inputCls} value={form.outcome_detail ?? ""} onChange={(e) => update("outcome_detail", e.target.value)} /></Field>
        </div>
      </Section>

      <Section title="Kundcitat">
        <Field label="Citat">
          <textarea rows={3} className={inputCls} value={form.client_quote ?? ""} onChange={(e) => update("client_quote", e.target.value)} />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Författare"><input className={inputCls} value={form.client_quote_author ?? ""} onChange={(e) => update("client_quote_author", e.target.value)} /></Field>
          <Field label="Roll"><input className={inputCls} value={form.client_quote_role ?? ""} onChange={(e) => update("client_quote_role", e.target.value)} /></Field>
        </div>
      </Section>

      <Section title="Brödtext">
        <Field label={`Markdown (${(form.body ?? "").length} tecken)`}>
          <textarea
            rows={18}
            className={`${inputCls} font-mono text-xs`}
            value={form.body ?? ""}
            onChange={(e) => update("body", e.target.value)}
          />
        </Field>
      </Section>
    </div>
  );
}
